import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { IconButton } from "@ui-kit/IconButton/IconButton";
import { ExternalIcon } from "@icons/ExternalIcon";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";

import "./AdaptiveModal.css";

interface AdaptiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
  height?: number;
}

/**
 * A modal or bottom sheet depending on if mobile dimensions or not.
 */
const AdaptiveModal: FunctionComponent<AdaptiveModalProps> = ({
  isOpen,
  onClose,
  children,
  width,
  height,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const {
    height: screenHeight,
    width: screenWidth,
    isMobile,
  } = useWindowSize();

  const maxHeight = useMemo(() => {
    return screenHeight * 0.9;
  }, [screenHeight]);

  const widthUseable = useMemo(() => {
    if (isMobile) {
      return screenWidth;
    }
    const maxWidth = screenWidth * 0.9;
    return !width || width > maxWidth ? maxWidth : width;
  }, [width, isMobile, screenWidth]);

  useEffect(() => {
    if (isOpen) {
      // Make visible first
      setIsVisible(true);

      // Wait for next frame to ensure element is in DOM
      requestAnimationFrame(() => {
        // Set initial state
        if (modalRef.current) {
          modalRef.current.style.opacity = "0";
        }
        if (contentRef.current) {
          contentRef.current.style.transform = "scale(0.8)";
          contentRef.current.style.opacity = "0";
        }

        // Force reflow to ensure initial state is applied
        modalRef.current?.offsetHeight;

        // Then animate in
        requestAnimationFrame(() => {
          if (modalRef.current) {
            modalRef.current.style.opacity = "1";
          }
          if (contentRef.current) {
            contentRef.current.style.transform = "scale(1)";
            contentRef.current.style.opacity = "1";
          }
        });
      });
    } else if (modalRef.current && contentRef.current) {
      // Animate out
      modalRef.current.style.opacity = "0";
      contentRef.current.style.transform = "scale(0.8)";
      contentRef.current.style.opacity = "0";

      // Wait for animation to complete before removing from DOM
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    // Handle ESC key to close modal
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = useCallback(
    // Close when clicking outside content area
    (event: React.MouseEvent) => {
      if (modalRef.current === event.target) {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    },
    [onClose],
  );

  if (!isVisible && !isOpen) {
    return null;
  }

  return (
    <div
      className={`adaptive-modal-overlay ${isMobile && "mobile"}`}
      ref={modalRef}
      onClick={handleBackdropClick}
      style={{ transition: "opacity 0.3s ease" }}>
      <div
        className="adaptive-modal-content"
        ref={contentRef}
        style={{
          transition: "transform 0.3s ease, opacity 0.3s ease",
          width: `${widthUseable}px`,
          maxHeight: `${maxHeight}px`,
          height: height ? `${height}px` : "auto",
        }}>
        {isOpen && (
          <div className="adaptive-modal-close">
            <IconButton
              icon={
                <ExternalIcon
                  svgName="close"
                  size={20}
                  color={colors.neutral700}
                />
              }
              onPress={onClose}
              type="raised"
            />
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export { AdaptiveModal };
